import { SnapRect } from 'hooks/useSmartGuide/useSmartGuide.types';
import { BoundingBoxRef } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { WidgetId } from 'types/idTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { isWidgetWrapping } from 'widgets/WidgetLayout.helpers';
import { ResponsiveGroupBatchUpdateType, ResponsiveShapeResizeType } from 'widgets/store';

/**
 * Resizes and shifts position of the member widgets of a group in response to a change in the text widget height (increase or decrease)
 *
 * Obs: mutates the member widgets topPx/size on the page
 * adds the widget data to the batch update to be dispatched to the store with the next text widget update
 *
 * See more: useResponsiveGroup.ts
 *
 * @param boundingBox
 * @param memberWidgetIds
 * @param parentId
 * @param widgetId
 * @param textWidgetHeight
 * @param textWidgetPreviousHeight
 * @param responsiveGroupBatchUpdate
 * @param responsiveShapeResize
 *
 * @returns void
 */

export const resizeWidgets = ({
  boundingBox,
  memberWidgetIds,
  parentId,
  widgetId,
  textWidgetHeight,
  textWidgetPreviousHeight,
  responsiveGroupBatchUpdate,
  responsiveShapeResize,
  customEl,
}: {
  boundingBox: BoundingBoxRef;
  memberWidgetIds: WidgetId[];
  parentId: WidgetId;
  widgetId: WidgetId;
  textWidgetHeight: number;
  textWidgetPreviousHeight: number;
  responsiveGroupBatchUpdate: ResponsiveGroupBatchUpdateType;
  responsiveShapeResize: ResponsiveShapeResizeType;
  customEl: HTMLElement | null;
}) => {
  const widgetEl = customEl || document.getElementById(widgetId);
  if (!widgetEl) return;

  const textWidgetTopPx = parseFloat(widgetEl.style.top);
  const textWidgetLeftPx = parseFloat(widgetEl.style.left);
  const textWidgetBottomPx = textWidgetTopPx + textWidgetPreviousHeight;
  const textWidgetHeightDiff = textWidgetHeight - textWidgetPreviousHeight;

  // if the height hasn't changed - skip the update
  if (textWidgetHeightDiff === 0) return;

  const textWidthPx = widgetEl.clientWidth;

  // Iterate over the group members and update the below widgets and the wrapping basic shape widgets
  for (let i = 0; i < memberWidgetIds.length; i++) {
    const memberWidgetId = memberWidgetIds[i];

    // skip self
    if (memberWidgetId === widgetId) continue;
    const memberWidgetEl = document.getElementById(memberWidgetId);
    if (!memberWidgetEl) continue;

    // 1. The widget is below (or will be below) -> update the topPx relative to the height difference
    // skip this for undo/redo actions while editing the text widget
    const memberWidgetTopPx = parseFloat(memberWidgetEl.style.top);
    const isBelow = memberWidgetTopPx >= textWidgetBottomPx;
    const memberType = getWidgetTypeFromId(memberWidgetId);

    if (isBelow) {
      const memberWidgetTopPx = parseFloat(memberWidgetEl.style.top);
      const memberWidgetNewTopPx = memberWidgetTopPx + textWidgetHeightDiff;
      memberWidgetEl.style.top = `${memberWidgetNewTopPx}px`;

      // add the new widget data to the batch update
      const widgetData = { topPx: memberWidgetNewTopPx };
      responsiveGroupBatchUpdate.addWidgetData(parentId, memberWidgetId, widgetData);

      // in case the bounding box has updated - update it just in case
      boundingBox.updateRect();
    }

    // 2. For basic shapes that wrap around the text widget, update the height of the basic shape
    if (memberType === WidgetType.BasicShape) {
      const shapeWidthPx = parseFloat(memberWidgetEl.style.width);
      const shapeHeightPx = parseFloat(memberWidgetEl.style.height);
      const shapeTopPx = parseFloat(memberWidgetEl.style.top);
      const shapeLeftPx = parseFloat(memberWidgetEl.style.left);

      const shapeSize = { topPx: shapeTopPx, leftPx: shapeLeftPx, widthPx: shapeWidthPx, heightPx: shapeHeightPx };
      const textSize = {
        topPx: textWidgetTopPx,
        leftPx: textWidgetLeftPx,
        widthPx: textWidthPx,
        heightPx: textWidgetPreviousHeight,
      };

      const isWrapping = isWidgetWrapping(shapeSize, textSize);

      // if it is wrapping -> increase height by the height difference
      if (isWrapping) {
        let memberWidgetNewHeightPx = shapeHeightPx + textWidgetHeightDiff;
        const result = responsiveShapeResize.resize(memberWidgetId, shapeWidthPx, memberWidgetNewHeightPx, {
          isResponsiveGroupResize: true,
          contentHeightPx:
            computeNewContentHeight(memberWidgetIds, shapeSize, memberWidgetId, {
              id: widgetId,
              newHeightPx: textWidgetHeight,
              previousSize: textSize,
            }) || 0,
        });
        let widgetData: Partial<Widget> = { heightPx: memberWidgetNewHeightPx };
        if (!result) {
          memberWidgetEl.style.height = `${memberWidgetNewHeightPx}px`;
        } else if (shapeWidthPx !== result.widthPx || memberWidgetNewHeightPx !== result.heightPx) {
          // If the shape dimensions were changed by the resize function - (i.e. for 1:1 shapes)
          // Update the shape position - assumes that the shape is centered around its contents
          const { widthPx, heightPx } = result;
          const newTopPx = shapeTopPx + (memberWidgetNewHeightPx - heightPx) / 2;
          const newLeftPx = shapeLeftPx + (shapeWidthPx - widthPx) / 2;
          memberWidgetEl.style.width = `${widthPx}px`;
          memberWidgetEl.style.height = `${heightPx}px`;
          memberWidgetEl.style.left = `${newLeftPx}px`;
          memberWidgetEl.style.top = `${newTopPx}px`;

          widgetData = { widthPx, heightPx, topPx: newTopPx, leftPx: newLeftPx };
        }

        // add the new widget data to the batch update
        responsiveGroupBatchUpdate.addWidgetData(parentId, memberWidgetId, widgetData);

        boundingBox.updateRect();
      }
    }
  }
};

/**
 * Compute the new content height of a shape widget after changes to `changedWidget`.
 * Content refers to widgets wrapped by the shape
 *
 * @param memberWidgetIds Widgets in group
 * @param shapeSize Shape size
 * @param shapeId Basic shape widget id
 * @param changedWidget Data for the changed widget
 * @returns number - new content heiight after the changes
 */
export const computeNewContentHeight = (
  memberWidgetIds: WidgetId[],
  shapeSize: SnapRect,
  shapeId: WidgetId,
  changedWidget: {
    id: WidgetId;
    newHeightPx: number;
    previousSize: SnapRect;
  },
) => {
  const { id: textWidgetId, newHeightPx, previousSize } = changedWidget;
  const textWidgetHeightDiff = newHeightPx - previousSize.heightPx;

  // Compute the content height prior to changes to `changedWidget`
  let lowestWidgetBottomPx = undefined;
  let highestWidgetTopPx = undefined;
  for (let j = 0; j < memberWidgetIds.length; j++) {
    const memberWidgetId = memberWidgetIds[j];

    // Skip current shape
    if (memberWidgetId === shapeId) continue;

    // Skip other shapes
    const type = getWidgetTypeFromId(memberWidgetId);
    if (type === WidgetType.BasicShape) continue;

    const memberEl = document.getElementById(memberWidgetId);
    if (!memberEl) continue;

    let memberSize = {
      topPx: parseFloat(memberEl.style.top),
      leftPx: parseFloat(memberEl.style.left),
      widthPx: memberEl.clientWidth,
      heightPx: memberEl.clientHeight,
    };

    // use the previous size for the changed widget
    if (memberWidgetId === textWidgetId) {
      memberSize = previousSize;
    }

    // check if the other widgets are wrapping around this shape
    if (isWidgetWrapping(shapeSize, memberSize)) {
      const memberBottomPx = memberSize.topPx + memberSize.heightPx;

      if (typeof lowestWidgetBottomPx === 'undefined') {
        lowestWidgetBottomPx = memberBottomPx;
      }

      if (typeof highestWidgetTopPx === 'undefined') {
        highestWidgetTopPx = memberSize.topPx;
      }

      if (memberBottomPx > lowestWidgetBottomPx) {
        lowestWidgetBottomPx = memberBottomPx;
      }
      if (memberSize.topPx < highestWidgetTopPx) {
        highestWidgetTopPx = memberSize.topPx;
      }
    }
  }

  // New height is the prev height + the height difference of the text widget
  const previousContentHeight = (lowestWidgetBottomPx as number) - (highestWidgetTopPx as number);
  return previousContentHeight + textWidgetHeightDiff;
};
